import type { TournamentCompetition, TournamentEntry, TournamentMatch } from "@mixitone/models";
import { isNil, mapValues } from "@mixitone/util";

export function roundRobinGroupsLocked(competition: TournamentCompetition | null | undefined): boolean {
  return competition?.format === "RoundRobin" && competition.format_data?.groupsLocked === true;
}

export function roundRobinHasGroups(competition: TournamentCompetition | null | undefined): boolean {
  const groups = competition?.format_data?.groups;
  if (!groups || typeof groups !== "object") return false;
  return Object.values(groups as Record<string, unknown>).some(
    (entryIds) => Array.isArray(entryIds) && entryIds.length > 0,
  );
}

export async function setRoundRobinGroupsLocked(
  competition: TournamentCompetition,
  locked: boolean,
): Promise<void> {
  if (competition.format !== "RoundRobin") {
    throw new Error("Group locking is only available for round robin competitions");
  }

  if (roundRobinGroupsLocked(competition) === locked) return;

  if (!competition.format_data) {
    competition.format_data = {};
  }
  competition.format_data.groupsLocked = locked;
  competition.changed("format_data");
  await competition.save();
}

export function roundRobinPlayoffSettings(competition: TournamentCompetition | null | undefined): {
  enablePlayoffs: boolean;
  divisions: number;
} {
  const formatSettings = competition?.format_settings as
    | { enablePlayoffs?: boolean; divisions?: number }
    | undefined;
  const divisions = formatSettings?.divisions ?? 1;
  return {
    enablePlayoffs: competition?.format === "RoundRobin" && formatSettings?.enablePlayoffs === true,
    divisions: typeof divisions === "number" && divisions >= 1 ? divisions : 1,
  };
}

export function groupStageMatchesComplete(matches: Iterable<TournamentMatch>): boolean {
  const groupMatches: TournamentMatch[] = [];
  for (const match of matches) {
    const phase = match.format_data?.phase;
    if (phase === "playoff") continue;
    if (phase === "group" || match.format_data?.group) {
      groupMatches.push(match);
    }
  }
  if (groupMatches.length === 0) return false;
  return groupMatches.every((match) => match.isResolved);
}

/** True when a round has matches and every match is completed or voided. */
export function roundMatchesResolved(matches: TournamentMatch[]): boolean {
  return matches.length > 0 && matches.every((match) => match.isResolved);
}

export function playoffMatchesStarted(matches: Iterable<TournamentMatch>): boolean {
  for (const match of matches) {
    if (match.format_data?.phase !== "playoff") continue;
    if (match.isInProgress || match.isComplete || match.isVoided) return true;
  }
  return false;
}

/**
 * Update an entry's group assignment and sync with the competition's groups metadata.
 * This is format-specific logic for RoundRobin competitions.
 * @param entry The tournament entry to update
 * @param group The group name (e.g., "Group 1") or undefined to remove the group
 * @param competition The competition this entry belongs to
 */
export async function setEntryGroupAndSync(
  entry: TournamentEntry,
  newGroup: string | undefined,
  competition: TournamentCompetition,
): Promise<void> {
  if (!entry.id) {
    throw new Error("Cannot set group on unsaved entry");
  }

  if (competition.format !== "RoundRobin") {
    throw new Error("setEntryGroupAndSync is only applicable for RoundRobin format competitions");
  }

  if (entry.group !== newGroup) {
    console.log("[GroupWrite][tournamentHelpers.setEntryGroupAndSync]", {
      entryId: entry.id,
      competitionId: competition.id,
      previousGroup: entry.group,
      nextGroup: newGroup,
    });
    entry.group = newGroup;
    await entry.save();
  }

  await syncEntryGroupToCompetition(entry, competition);
}

export async function syncEntryGroupToCompetition(
  entry: TournamentEntry,
  competition: TournamentCompetition,
) {
  if (!entry.id) {
    throw new Error("Cannot sync entry group to competition on unsaved entry");
  }

  if (competition.format !== "RoundRobin") {
    throw new Error("syncEntryGroupToCompetition is only applicable for RoundRobin format competitions");
  }

  if (isNil(competition.format_data)) {
    competition.format_data = {};
  }

  const newGroup = entry.group;

  const groups = mapValues(
    (competition.format_data?.groups ?? {}) as Record<string, string[]>,
    (ary) => new Set(ary),
  );

  let changedMade = false;
  if (newGroup && !groups[newGroup]) {
    groups[newGroup] = new Set();
    changedMade = true;
  }
  Object.entries(groups).forEach(([group, entries]) => {
    if (group === newGroup && !entries.has(entry.id!)) {
      entries.add(entry.id!);
      changedMade = true;
    } else if (group !== newGroup && entries.has(entry.id!)) {
      entries.delete(entry.id!);
      changedMade = true;
    }
  });

  if (changedMade) {
    for (const [group, entries] of Object.entries(groups)) {
      if (entries.size === 0) {
        delete groups[group];
      }
    }

    competition.format_data.groups = mapValues(groups, (ary) => Array.from(ary));
    competition.changed("format_data");
    await competition.save();
  }
}
